import { ViewContainerRef } from '@angular/core';
import { CartLine, OrderTypes } from '@next/core-lib/cart';

import { CartComponent } from '../cart.component';
import { createColumns } from './create-column-functions/create-columns';
import { ColumnBlockedForReturnComponent } from '../components/column-blocked-for-return/column-blocked-for-return.component';
import { ColumnDeliveryDateComponent } from '../components/column-delivery-date/column-delivery-date.component';
import { ColumnDeliveryPeriodComponent } from '../components/column-delivery-period/column-delivery-period.component';
import { ColumnExternalInvoiceIdForReturnComponent } from '../components/column-external-invoice-id-for-return/column-external-invoice-id-for-return.component'; // eslint-disable-line max-len
import { ColumnPriceResultsComponent } from '../components/column-price-results/column-price-results.component';
import { ColumnProductSupplementsComponent } from '../components/column-product-supplements/column-product-supplements.component';
import { ColumnQuantityUnitInformationComponent } from '../components/column-quantity-unit-information/column-quantity-unit-information.component'; // eslint-disable-line max-len
import { ColumnRemarksComponent } from '../components/column-remarks/column-remarks.component';
import { ColumnSiloDistributionComponent } from '../components/column-silo-distribution/column-silo-distribution.component';
import { ColumnSkuTitleComponent } from '../components/column-sku-title/column-sku-title.component';
import { LabelDeliveryDateComponent } from '../components/label-delivery-date/label-delivery-date.component';
import { SubrowNotificationsComponent } from '../components/subrow-notifications/subrow-notifications.component';

export const createColumnComponents = (componentRef: CartComponent, container: ViewContainerRef) => {
  componentRef.columnSkuTitleRef = container.createComponent(ColumnSkuTitleComponent);
  componentRef.columnSkuTitleRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnQuantityAndUnitInformationRef = container.createComponent(ColumnQuantityUnitInformationComponent);
  componentRef.columnQuantityAndUnitInformationRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnSiloDistributionRef = container.createComponent(ColumnSiloDistributionComponent);
  componentRef.columnSiloDistributionRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnProductSupplementsRef = container.createComponent(ColumnProductSupplementsComponent);
  componentRef.columnProductSupplementsRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnDeliveryDateRef = container.createComponent(ColumnDeliveryDateComponent);
  componentRef.columnDeliveryDateRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.labelDeliveryDateRef = container.createComponent(LabelDeliveryDateComponent);
  componentRef.labelDeliveryDateRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnDeliveryPeriodRef = container.createComponent(ColumnDeliveryPeriodComponent);
  componentRef.columnDeliveryPeriodRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnExternalInvoiceIdForReturnRef = container.createComponent(ColumnExternalInvoiceIdForReturnComponent);
  componentRef.columnExternalInvoiceIdForReturnRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnPriceResultsRef = container.createComponent(ColumnPriceResultsComponent);
  componentRef.columnPriceResultsRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.cartLineSubrowRef = container.createComponent(SubrowNotificationsComponent);
  componentRef.cartLineSubrowRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnRemarksRef = container.createComponent(ColumnRemarksComponent);
  componentRef.columnRemarksRef.instance.initCallback = componentRef.updateTableConfig;

  componentRef.columnBlockedForReturnRef = container.createComponent(ColumnBlockedForReturnComponent);
  componentRef.columnBlockedForReturnRef.instance.initCallback = componentRef.updateTableConfig;
};

export const tableIsInitiated = (componentRef: CartComponent) =>
  !!(
    componentRef.cart &&
    !componentRef.tableConfig.columns &&
    componentRef.columnSkuTitleRef.instance.template &&
    componentRef.columnExternalInvoiceIdForReturnRef.instance.template &&
    componentRef.columnQuantityAndUnitInformationRef.instance.template &&
    componentRef.columnSiloDistributionRef.instance.template &&
    componentRef.columnProductSupplementsRef.instance.template &&
    componentRef.columnDeliveryDateRef.instance.template &&
    componentRef.columnDeliveryPeriodRef.instance.template &&
    componentRef.columnPriceResultsRef.instance.template &&
    componentRef.cartLineSubrowRef.instance.template &&
    componentRef.columnRemarksRef.instance.template
  );

export const setColumnValues = (componentRef: CartComponent) => {
  componentRef.columnDeliveryDateRef.instance.cartId = componentRef.cart?.id;
  componentRef.columnPriceResultsRef.instance.cartStatus = componentRef.cart?.status;
  componentRef.columnSiloDistributionRef.instance.cartId = componentRef.cart?.id;
  componentRef.columnProductSupplementsRef.instance.cartId = componentRef.cart?.id;

  componentRef.columnDeliveryDateRef.instance.editable = componentRef.editable;
  componentRef.columnSiloDistributionRef.instance.editable = componentRef.editable;
  componentRef.columnProductSupplementsRef.instance.editable = componentRef.editable;
  componentRef.columnQuantityAndUnitInformationRef.instance.editable = componentRef.editable;

  componentRef.columnDeliveryDateRef.instance.orderType = componentRef.cart?.type;
  componentRef.cartLineSubrowRef.instance.orderType = componentRef.cart?.type;

  componentRef.columnRemarksRef.instance.cartId = componentRef.cart?.id;
};

export const setTableValues = (
  componentRef: CartComponent,
  hasAtLeastAdvisorRole: boolean,
  featureFlags: string[],
  isTabOrSmallerDisplay: boolean,
  hasRemarksFeature: boolean,
) => {
  componentRef.tableConfig.trackBy = (_: number, line: CartLine) => line.id;

  componentRef.tableConfig.fullWidthRowTemplate = componentRef.cartLineSubrowRef.instance.template;
  componentRef.tableConfig.columns = createColumns(componentRef, hasAtLeastAdvisorRole, featureFlags, isTabOrSmallerDisplay, hasRemarksFeature);

  componentRef.tableConfig.actions = [
    ...(componentRef.cart?.type !== OrderTypes.preSalesOrder && componentRef.editable
      ? [
          {
            id: 'remove',
            callback: (line: CartLine) => componentRef.deleteLine(line.id),
            label: '',
            icon: 'delete_outline',
            disabled: () => false,
            primary: true,
          },
        ]
      : []),
  ];
};
